import {
  transformExportParamsToCrudApi,
  transformParamsToCrudApi,
} from '@app/core/crud/listing-adapter';
import { convertGetManyResponse } from '@app/core/crud/response-converter';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { ListViewInfinite } from '@app/core/list-view/ListViewInfinite';
import { ListViewExportParams, ListViewQueryParams } from '@app/core/list-view/types';
import { FragmentListItemDto } from '@app/modules/fragments/fragment.types';
import { webAppGetFragmentsList } from '@mi-front/api-v2';
import { Paginated, Station, cleanQuery } from '@mi-front/core';
import fragmentApiClient from 'libs/api-clients/src/clients/fragments-api-client';
import { FRAGMENTS_FILTERS_CONFIG } from './filters/fragments-filters-config';
import { FragmentGridViewToggle } from './fragments-grid/FragmentGridViewToggle';
import { FragmentsGrid } from './fragments-grid/FragmentsGrid';
import { useFragmentsAddFilterTour } from './useFragmentsAddFilterTour';

/**
 * Main list component for Fragments List V3.
 * Uses BFF fragments list API with CRUD params and renders a studio grid of cards.
 * Now uses infinite scrolling with a "Load More" button via ListViewInfinite.
 */
export function FragmentsList({ station }: { station: Station }) {
  const timezone = useTimezone();
  const listId = `fragments-list-v3-${station.slug}`;

  useFragmentsAddFilterTour(listId);

  const queryFn = async (params: ListViewQueryParams): Promise<Paginated<FragmentListItemDto>> => {
    const processedParams = transformParamsToCrudApi(params, {
      filterDefinitions: FRAGMENTS_FILTERS_CONFIG,
      context: { timezone },
    });

    const response = await webAppGetFragmentsList({
      params: cleanQuery({
        ...processedParams,
        filter: [...processedParams.filter, `stationId||eq||${station.id}`],
      }),
    });

    return await convertGetManyResponse(response);
  };

  const exportFn = async (params: ListViewExportParams) => {
    const crudParams = transformExportParamsToCrudApi(params, {
      filterDefinitions: FRAGMENTS_FILTERS_CONFIG,
      context: { timezone },
    });

    return fragmentApiClient({
      method: 'GET',
      url: `/export/${station.slug}`,
      params: cleanQuery({
        ...crudParams,
        filter: [...(crudParams.filter ?? []), `stationId||eq||${station.id}`],
      }),
      responseType: 'arraybuffer',
    });
  };

  return (
    <ListViewInfinite
      listId={listId}
      filters={FRAGMENTS_FILTERS_CONFIG}
      queryFn={queryFn}
      exportFn={exportFn}
      ListComponent={FragmentsGrid}
      actions={[<FragmentGridViewToggle key="fragment-grid-layout" />]}
      showProgress
      maxPages={3}
    />
  );
}
